import { useAuth0 } from '@auth0/auth0-react'
import { Box, CardMedia, CircularProgress } from '@mui/material'
import LocalDiningIcon from '@mui/icons-material/LocalDining'
import React, { useEffect, useState } from 'react'
import useAppUtilities from '../../hooks/useAppUtilities'
import { IRestaurantDishRatingReview } from '../../pages/eater/restaurant/components/DishesTab'
import { IChipData } from '../../types'
import {
  RestaurantDishesCardCircularProgressWrapper,
  RestaurantDishesCardContent,
  RestaurantDishesCardContentDescriptionTypography,
  RestaurantDishesCardContentRating,
  RestaurantDishesCardContentRatingIconStarFilled,
  RestaurantDishesCardContentRatingIconStarFilledEmpty,
  RestaurantDishesCardContentTitleTypography,
  RestaurantDishesCardContentWrapper,
  RestaurantDishesCardContentWrapperPriceTypography,
  RestaurantDishesCardInnerContainer,
  RestaurantDishesCardMainContainer,
  RestaurantDishesCardMediaWrapper,
  RestaurantDishesCardSkeleton,
  RestaurantDishesCardWrapperCard,
} from '../styled/restaurant/RestaurantDishesCard.styled'
import CustomButton from '../ui/CustomButton'
import TagChip from '../ui/TagChip'

interface FoodCardProps {
  image: string
  title: string
  description: string
  isEditable?: boolean
  price?: number
  rating?: number
  isButton?: boolean
  id: string
  onRemove?: () => void
  onChangeRating?: (item: IRestaurantDishRatingReview) => void
  tagChipData?: IChipData[]
  isLoading?: boolean
  showMatchText?: boolean
}

const RestaurantDishesCard: React.FC<FoodCardProps> = ({
  image,
  rating,
  description,
  title,
  isEditable = false,
  isButton = false,
  price,
  id,
  onRemove,
  onChangeRating,
  showMatchText = true,
  tagChipData,
  isLoading,
}) => {
  const { theme } = useAppUtilities()
  const { isAuthenticated } = useAuth0()
  const [loaded, setLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [newRating, setNewRating] = React.useState<number | null>(rating ?? 0)

  const isLogoFallback = image === '/logoLetter-resize.svg' || image === ''
  const showPlaceholder = imageError || isLogoFallback

  useEffect(() => {
    if (rating) {
      setNewRating(rating)
    }
  }, [rating])

  const handleRemove = (): void => {
    if (onRemove && !isLoading) {
      onRemove()
    }
  }

  const onRating = (rating: number | null): void => {
    setNewRating(rating)
    if (onChangeRating)
      onChangeRating({ id, starCount: rating !== null ? rating : 0 })
  }

  return (
    <RestaurantDishesCardMainContainer>
      <RestaurantDishesCardWrapperCard isLoading={isLoading}>
        <RestaurantDishesCardInnerContainer>
          {showPlaceholder ? (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                borderRadius: '16px',
                border: `2px solid ${theme.palette.grey[200]}`,
                backgroundColor: '#f5f0eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalDiningIcon sx={{ fontSize: 36, color: '#c4b5a5' }} />
            </Box>
          ) : (
            <>
              {!loaded && <RestaurantDishesCardSkeleton />}
              <CardMedia
                sx={{
                  height: '100%',
                  width: '100%',
                  borderRadius: '16px',
                  objectFit: 'cover',
                  border: `2px solid ${theme.palette.grey[200]}`,
                  boxShadow: '0px 0px 2px 0px #1A1A1A40',
                  display: loaded ? 'block' : 'none',
                }}
                component="img"
                loading="eager"
                image={image}
                alt={`resactly-${title}-Dish`}
                onLoad={() => setLoaded(true)}
                onError={() => setImageError(true)}
              />
            </>
          )}
          <RestaurantDishesCardMediaWrapper>
            {tagChipData && (
              <TagChip
                data={tagChipData}
                bgColor="liteOrange"
                textBlur={!isAuthenticated}
                showMatchText={showMatchText}
              />
            )}
          </RestaurantDishesCardMediaWrapper>
        </RestaurantDishesCardInnerContainer>

        <RestaurantDishesCardContent>
          <div>
            <RestaurantDishesCardContentTitleTypography>
              {title.toLocaleLowerCase()}
            </RestaurantDishesCardContentTitleTypography>
            <RestaurantDishesCardContentDescriptionTypography>
              {description}
            </RestaurantDishesCardContentDescriptionTypography>
          </div>
          <RestaurantDishesCardContentWrapper>
            <>
              {Number(price) > 0 && (
                <RestaurantDishesCardContentWrapperPriceTypography>
                  {price}
                </RestaurantDishesCardContentWrapperPriceTypography>
              )}
              {typeof rating === 'number' && (
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <RestaurantDishesCardContentRating
                    name={isEditable ? 'simple-controlled' : 'read-only'}
                    value={newRating}
                    readOnly={!isLoading ? !isEditable : true}
                    onChange={(event, newValue) => {
                      event.stopPropagation()
                      onRating(newValue)
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    icon={<RestaurantDishesCardContentRatingIconStarFilled />}
                    emptyIcon={
                      <RestaurantDishesCardContentRatingIconStarFilledEmpty />
                    }
                  />
                </div>
              )}
              {isEditable && isButton && (
                <CustomButton
                  title="Remove"
                  backgroundColor={theme.palette.common.white}
                  textColor={theme.palette.primary.light}
                  hoverColor={theme.palette.primary.light}
                  sx={{
                    borderColor: theme.palette.grey[300],
                    borderRadius: '10px',
                  }}
                  onClick={handleRemove}
                />
              )}
            </>
          </RestaurantDishesCardContentWrapper>
        </RestaurantDishesCardContent>
      </RestaurantDishesCardWrapperCard>
      {isLoading && (
        <RestaurantDishesCardCircularProgressWrapper>
          <CircularProgress color="inherit" />
        </RestaurantDishesCardCircularProgressWrapper>
      )}
    </RestaurantDishesCardMainContainer>
  )
}

export default RestaurantDishesCard
